/**
 * `/users` — the directory.
 *
 * There is no delete, and that is the backend's decision rather than a gap here:
 * audit rows must keep naming people, and role assignments and resource grants
 * point at subjects with no foreign key to cascade. Accounts are **deactivated**
 * instead, which stops them signing in and leaves the history readable.
 *
 * Inactive accounts are hidden by default but the toggle is on screen, because
 * "that user has vanished" is a worse confusion than a slightly longer list.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { useUserActions } from '../entities/userActions';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useDebounced } from '../../hooks/useDebounced';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { createUser, listUsers } from '../../api/identity';

export default function UsersPage() {
  const { t } = useTranslation('people');
  const { can } = useAuth();

  const [term, setTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const debounced = useDebounced(term);

  // `resetKey` carries both filters: changing either changes *what* is listed,
  // and staying on page four of the previous result set would show an empty
  // table with nothing to explain it.
  const list = useListQuery({
    defaultSort: 'displayName',
    resetKey: [debounced, includeInactive],
  });

  const users = useAsync(
    () => listUsers({ q: debounced, includeInactive, ...list.query }),
    [debounced, includeInactive, list.key],
  );

  // Everything that can be done to an account, declared once in the entity file
  // and rendered here as row icons and on `UserDetailPage` as header buttons.
  // The two screens therefore offer the same set, which is the whole point:
  // "what can I do to this user?" must not have two answers.
  const actions = useUserActions({ onChanged: () => users.reload() });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '', password: '', requireChange: true });

  const create = useSubmit(async () => {
    const created = await createUser({
      email: form.email.trim(),
      displayName: form.displayName.trim(),
      // An empty box is not an empty password: omitting it creates an account
      // that simply cannot sign in yet, which is the sane default for somebody
      // whose credential will arrive another way.
      ...(form.password ? { password: form.password, requirePasswordChange: form.requireChange } : {}),
    });
    setForm({ email: '', displayName: '', password: '', requireChange: true });
    users.reload();
    return created;
  });

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.description')}
        actions={
          can('user.create') && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setCreating(true)}
            >
              {t('users.new')}
            </Button>
          )
        }
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <SearchField
          value={term}
          onChange={setTerm}
          placeholder={t('users.searchPlaceholder')}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
          }
          label={t('users.showDeactivated')}
        />
      </Stack>

      <AsyncContent state={users}>
        {({ users: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(user) => user.id}
            list={list}
            page={page}
            empty={{
              title: t('users.emptyTitle'),
              description: debounced ? t('users.emptySearched') : t('users.emptyPlain'),
              dense: true,
            }}
            columns={[
              {
                field: 'displayName',
                label: t('users.column.name'),
                sortable: true,
                render: (user) => (
                  <Link
                    component={RouterLink}
                    to={`/users/${user.id}`}
                    underline="hover"
                    color="inherit"
                    sx={{ fontWeight: 500 }}
                  >
                    {user.displayName}
                  </Link>
                ),
              },
              {
                field: 'email',
                label: t('users.column.email'),
                sortable: true,
                render: (user) => (
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                ),
              },
              // Not sortable: `isActive` is a filter, and the toggle above
              // answers "show me the deactivated ones" better than a two-value
              // sort would.
              {
                field: 'status',
                label: t('users.column.status'),
                width: 120,
                render: (user) => (
                  <Chip
                    size="small"
                    label={user.isActive ? t('users.active') : t('users.deactivated')}
                    color={user.isActive ? 'success' : 'default'}
                    variant={user.isActive ? 'filled' : 'outlined'}
                  />
                ),
              },
              {
                field: 'lastLoginAt',
                label: t('users.column.lastSignIn'),
                sortable: true,
                width: 160,
                render: (user) => (
                  <Typography variant="caption" color="text.secondary">
                    <Timestamp value={user.lastLoginAt} />
                  </Typography>
                ),
              },
            ]}
            actionsWidth={216}
            actions={actions.rowActions}
          />
        )}
      </AsyncContent>

      {/* An `immediate` action — reactivating an account — has no dialog to show
          its failure in, so it is shown here instead of vanishing. */}
      {actions.error && <ErrorState error={actions.error} />}

      {/* The row actions' dialogs. A table renders rows, not dialogs, so this
          line is the one thing the declaration cannot mount for a list screen —
          and an icon whose dialog was never mounted does nothing. */}
      {actions.dialogs}

      <FormDialog
        open={creating}
        title={t('users.createDialog.title')}
        submitLabel={t('users.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={form.email.trim() === '' || form.displayName.trim() === ''}
        onClose={() => setCreating(false)}
        onSubmit={async () => {
          if (await create.submit()) setCreating(false);
        }}
      >
        <TextField
          label={t('users.createDialog.email')}
          type="email"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
          autoFocus
          fullWidth
        />
        <TextField
          label={t('users.createDialog.displayName')}
          value={form.displayName}
          onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          fullWidth
        />
        <TextField
          label={t('users.createDialog.password')}
          type="password"
          value={form.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
          autoComplete="new-password"
          helperText={t('users.createDialog.passwordHelp')}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.requireChange}
              disabled={form.password === ''}
              onChange={(event) => setForm({ ...form, requireChange: event.target.checked })}
            />
          }
          label={t('users.createDialog.requireChange')}
        />
        <Box />
      </FormDialog>
    </>
  );
}
